/**
 * Month and trend helpers shared by the Cost of living hero (#1941, #1982) and
 * its month bars (#1942), so both name a month the same way.
 */

/**
 * The browser's current calendar month in LOCAL time, as YYYY-MM. Never a UTC
 * string: on the 1st the hero must already show the new month in the user's
 * zone, and the server never decides the month (#1940).
 * @param {Date} [now]
 * @returns {string}
 */
export const localYearMonth = (now = new Date()) =>
  `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

/**
 * Today's day of the month and the month's length, both in LOCAL time, for the
 * hero's "day D of N" (#1982).
 * @param {Date} [now]
 * @returns {{day: number, days: number}}
 */
export const dayOfMonth = (now = new Date()) => ({
  day: now.getDate(),
  days: new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate(),
});

const formatMonth = (yearMonth, locale, options) => {
  const [y, m] = String(yearMonth).split('-').map(Number);
  try {
    return new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' })
      .format(new Date(Date.UTC(y, m - 1, 1)));
  } catch {
    return yearMonth;
  }
};

/** "September 2026" for a YYYY-MM in the given locale. */
export const monthLabel = (yearMonth, locale) =>
  formatMonth(yearMonth, locale, { month: 'long', year: 'numeric' });

/**
 * "Oct '26" for a YYYY-MM in the given locale: short month, then an
 * apostrophe and the two-digit year, as the Month view table titles read
 * (#2143, #2208).
 */
export const shortMonthYear = (yearMonth, locale) => {
  const [y, m] = yearMonth.split('-').map(Number);
  const parts = new Intl.DateTimeFormat(locale, { month: 'short', year: '2-digit' }).formatToParts(new Date(y, m - 1, 1));
  const month = parts.find((p) => p.type === 'month')?.value || '';
  const year = parts.find((p) => p.type === 'year')?.value || '';
  return `${month} '${year}`;
};

/** "September" for a YYYY-MM in the given locale (the card header, #2031). */
export const monthName = (yearMonth, locale) =>
  formatMonth(yearMonth, locale, { month: 'long' });

/** "Sep 26" for a YYYY-MM in the given locale (bar labels and caption). */
export const shortMonthLabel = (yearMonth, locale) =>
  formatMonth(yearMonth, locale, { month: 'short', year: '2-digit' });

/**
 * The bars' title (#1982): the window's first month to today, "Oct 2024 - Oct
 * 3, 2026" in the given locale. Today is the browser's LOCAL date. Joined with
 * a plain hyphen rather than Intl formatRange, which emits an en dash.
 * @param {string} fromYearMonth  YYYY-MM, the oldest month shown
 * @param {string} locale
 * @param {Date} [now]
 * @returns {string}
 */
export const historyRange = (fromYearMonth, locale, now = new Date()) => {
  const from = formatMonth(fromYearMonth, locale, { month: 'short', year: 'numeric' });
  let to;
  try {
    to = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', year: 'numeric' }).format(now);
  } catch {
    to = localYearMonth(now);
  }
  return `${from} - ${to}`;
};

/**
 * The trend over finished months (#1982): an ordinary least-squares fit of
 * `netCents` against the month's index. Months with no lines are left out, so
 * an empty slot never pulls the line toward zero; the caller leaves out the
 * current, partial month.
 * @param {Array<{netCents: number, lineCount: number}>} months  oldest first
 * @returns {null | {slopeCents: number, fromIndex: number, toIndex: number,
 *   fromCents: number, toCents: number}}  null with fewer than 2 months of
 *   lines; `slopeCents` is the change per month rounded to a cent, and the
 *   line runs from the first to the last fitted month.
 */
export const netTrend = (months) => {
  const points = [];
  months.forEach((m, i) => {
    if ((m.lineCount || 0) > 0) points.push({ x: i, y: m.netCents || 0 });
  });
  if (points.length < 2) return null;
  const meanX = points.reduce((s, p) => s + p.x, 0) / points.length;
  const meanY = points.reduce((s, p) => s + p.y, 0) / points.length;
  let sxx = 0;
  let sxy = 0;
  points.forEach((p) => {
    sxx += (p.x - meanX) ** 2;
    sxy += (p.x - meanX) * (p.y - meanY);
  });
  const slope = sxy / sxx;
  const at = (x) => meanY + slope * (x - meanX);
  const fromIndex = points[0].x;
  const toIndex = points[points.length - 1].x;
  return {
    slopeCents: Math.round(slope) || 0,
    fromIndex,
    toIndex,
    fromCents: at(fromIndex),
    toCents: at(toIndex),
  };
};
